// src/components/Assets/AssetRequests/RequestAssetForm.js
// Form for farmers to request asset permissions

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes, FaTools, FaCalendar, FaStickyNote } from "react-icons/fa";
import PropTypes from "prop-types";
import "../style.css";

const RequestAssetForm = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  assets = []
}) => {
  const [t] = useTranslation();

  const [formData, setFormData] = useState({
    assetId: "",
    requestStartDate: "",
    requestEndDate: "",
    notes: ""
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setFormData({
        assetId: "",
        requestStartDate: "",
        requestEndDate: "",
        notes: ""
      });
      setErrors({});
    }
  }, [isOpen]);

  const validate = () => {
    const newErrors = {};

    if (!formData.assetId) {
      newErrors.assetId = t("Please select an asset");
    }

    if (!formData.requestStartDate) {
      newErrors.requestStartDate = t("Start date is required");
    }

    if (!formData.requestEndDate) {
      newErrors.requestEndDate = t("End date is required");
    }

    if (formData.requestStartDate && formData.requestEndDate) {
      const startDate = new Date(formData.requestStartDate);
      const endDate = new Date(formData.requestEndDate);

      if (endDate < startDate) {
        newErrors.requestEndDate = t("End date must be after start date");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Convert date strings to timestamps and IDs to numbers
    // Note: Backend expects "requestDate" not "requestStartDate"
    const submissionData = {
      assetId: parseInt(formData.assetId, 10),
      requestDate: new Date(formData.requestStartDate).getTime(),
      requestEndDate: new Date(formData.requestEndDate).getTime(),
      notes: formData.notes
    };

    onSubmit(submissionData);
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains("modal-overlay")) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content request-form-modal">
        {/* Modal Header */}
        <div className="modal-header">
          <h2>{t("Request Asset Permission")}</h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <FaTimes />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="asset-form">
          {/* Asset Selection */}
          <div className="form-group">
            <label className="form-label">
              <FaTools /> {t("Select Asset")} <span className="required">*</span>
            </label>
            <select
              name="assetId"
              value={formData.assetId}
              onChange={handleChange}
              className={`form-input ${errors.assetId ? "error" : ""}`}
              disabled={isSubmitting}
            >
              <option value="">{t("Select an asset")}</option>
              {assets.map(asset => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                </option>
              ))}
            </select>
            {errors.assetId && (
              <span className="error-message">{errors.assetId}</span>
            )}
          </div>

          {/* Date Range */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <FaCalendar /> {t("From Date")} <span className="required">*</span>
              </label>
              <input
                type="date"
                name="requestStartDate"
                value={formData.requestStartDate}
                onChange={handleChange}
                className={`form-input ${errors.requestStartDate ? "error" : ""}`}
                disabled={isSubmitting}
              />
              {errors.requestStartDate && (
                <span className="error-message">{errors.requestStartDate}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FaCalendar /> {t("To Date")} <span className="required">*</span>
              </label>
              <input
                type="date"
                name="requestEndDate"
                value={formData.requestEndDate}
                onChange={handleChange}
                className={`form-input ${errors.requestEndDate ? "error" : ""}`}
                disabled={isSubmitting}
              />
              {errors.requestEndDate && (
                <span className="error-message">{errors.requestEndDate}</span>
              )}
            </div>
          </div>

          {/* Details/Notes */}
          <div className="form-group">
            <label className="form-label">
              <FaStickyNote /> {t("Details")}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="form-input"
              placeholder={t("Enter details")}
              rows="4"
              disabled={isSubmitting}
            />
          </div>

          {/* Submit Button */}
          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("Submitting...") : t("Submit Request")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

RequestAssetForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  assets: PropTypes.array
};

export default RequestAssetForm;
